/**
 * Post-time bell — a short synthesized "ding!" played when a track
 * crosses into the ≤2m imminent window.
 *
 * Uses Web Audio synthesis rather than an MP3 so there is no asset to
 * ship and no autoplay-policy flicker. Browsers restrict audio until
 * the user has interacted with the page, so the first play attempt
 * silently no-ops if the AudioContext can't resume. Any later chip
 * click (which is a user gesture) will resume the context and the
 * next bell plays normally.
 */

let _ctx = null;
const _getCtx = () => {
  if (_ctx) return _ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    _ctx = new Ctor();
  } catch {
    _ctx = null;
  }
  return _ctx;
};

// A single bell "ping" — two detuned sine oscillators with an
// exponential-decay envelope. Lightweight (~1.2s total) and reads as
// a real bell rather than a square-wave buzz.
const _ding = (ctx, when, freq, gain) => {
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, when);
  env.gain.setValueAtTime(0.0001, when);
  env.gain.exponentialRampToValueAtTime(gain, when + 0.01);
  env.gain.exponentialRampToValueAtTime(0.0001, when + 1.1);
  osc.connect(env);
  env.connect(ctx.destination);
  osc.start(when);
  osc.stop(when + 1.15);
};

export const playPostBell = () => {
  const ctx = _getCtx();
  if (!ctx) return;
  // Nudge the context in case the browser suspended it after a tab
  // switch. If this fails (no user gesture yet) the ding simply
  // doesn't produce audible output.
  if (ctx.state === "suspended") {
    try {
      ctx.resume();
    } catch {
      /* ignore */
    }
  }
  const t = ctx.currentTime;
  // Two detuned bells 7ms apart gives a shimmery racetrack-bell feel
  // without needing an actual sample.
  _ding(ctx, t, 1760, 0.22); // A6
  _ding(ctx, t + 0.007, 2637, 0.14); // E7
  _ding(ctx, t + 0.14, 1319, 0.1); // E6 — low after-ring
};
